/**
 * PRpillar fork: shared value-axis scale across dashboard tiles.
 *
 * A dashboard can declare groups of (tile, value axis) pairs that must share
 * one scale: same min/max, same tick step and the same plot-area height, so
 * that the same bar height means the same number on every chart of the group.
 *
 * The config lives in `DashboardConfig.axisScaleGroups`. Members point at a
 * tile by uuid in the API and by tile slug in dashboard-as-code YAML, exactly
 * like `dateZoomConfig.tileTargets`.
 *
 * Everything in this file is pure: the frontend feeds it the final ECharts
 * option of each member and gets back the extent of the member and the
 * shared domain of the group.
 */
export const getLdAxisScaleMemberAxis = (member) => member.axis ?? 0;
/**
 * Rewrites the tile reference of every member (uuid -> slug on download,
 * slug -> uuid on upload). Members whose tile can't be resolved are dropped:
 * a group must never point at a tile that is not on the dashboard.
 */
export const mapLdAxisScaleGroupTiles = (config, mapTile) => {
    if (!config?.axisScaleGroups)
        return config;
    return {
        ...config,
        axisScaleGroups: config.axisScaleGroups.map((group) => ({
            ...group,
            members: (group.members ?? []).reduce((acc, member) => {
                const tile = mapTile(member.tile);
                if (tile)
                    acc.push({ ...member, tile });
                return acc;
            }, []),
        })),
    };
};
/** Groups (and the axis inside each) that a tile takes part in */
export const getLdAxisScaleMemberships = (groups, tileUuid) => {
    if (!groups || !tileUuid)
        return [];
    return groups.flatMap((group) => (group.members ?? [])
        .filter((member) => member.tile === tileUuid)
        .map((member) => ({
        group,
        axis: getLdAxisScaleMemberAxis(member),
    })));
};
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const asArray = (value) => {
    if (Array.isArray(value))
        return value;
    if (value === undefined || value === null)
        return [];
    return [value];
};
const isDimensionAxis = (axis) => isRecord(axis) && (axis.type === 'category' || axis.type === 'time');
/**
 * Horizontal charts put the category on the y axis and the values on x.
 */
export const isLdHorizontalChart = (option) => isDimensionAxis(asArray(option.yAxis)[0]) &&
    !isDimensionAxis(asArray(option.xAxis)[0]);
const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '')
        return undefined;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : undefined;
};
const firstKey = (value) => {
    const key = Array.isArray(value) ? value[0] : value;
    return typeof key === 'string' || typeof key === 'number'
        ? String(key)
        : undefined;
};
/**
 * Extent of the values drawn on one value axis of a final ECharts option.
 *
 * - Stacked series (same `stack`) are summed per category, positives and
 *   negatives apart, like ECharts' `samesign` stacking: the height of a stack
 *   is what counts, not its biggest segment.
 * - Series side by side (no stack, or different stacks) count one bar each:
 *   All / Qualified / Won stacks are never added together.
 * - Series hidden in the legend still count: hiding a series must not
 *   rescale the group.
 * - Helper series with the tooltip switched off (stack-total labels, labels
 *   pinned to an axis max) are skipped: they carry no data of their own.
 *
 * Returns undefined when nothing is drawn on the axis.
 */
export const getLdValueAxisExtent = (option, axisIndex) => {
    if (!isRecord(option))
        return undefined;
    const horizontal = isLdHorizontalChart(option);
    const valuePos = horizontal ? 0 : 1;
    const categoryPos = horizontal ? 1 : 0;
    const dataset = asArray(option.dataset)[0];
    const rows = isRecord(dataset) ? asArray(dataset.source) : [];
    const positive = new Map();
    const negative = new Map();
    let hasValue = false;
    asArray(option.series).forEach((serie, serieIndex) => {
        if (!isRecord(serie))
            return;
        if (isRecord(serie.tooltip) && serie.tooltip.show === false)
            return;
        const serieAxis = toFiniteNumber(horizontal ? serie.xAxisIndex : serie.yAxisIndex);
        if ((serieAxis ?? 0) !== axisIndex)
            return;
        const stackKey = typeof serie.stack === 'string' && serie.stack !== ''
            ? `stack:${serie.stack}`
            : `serie:${serieIndex}`;
        const add = (category, rawValue) => {
            const value = toFiniteNumber(rawValue);
            if (value === undefined)
                return;
            hasValue = true;
            const key = `${stackKey}\u0000${String(category)}`;
            const bucket = value >= 0 ? positive : negative;
            bucket.set(key, (bucket.get(key) ?? 0) + value);
        };
        if (Array.isArray(serie.data)) {
            serie.data.forEach((item, index) => {
                const raw = isRecord(item) ? item.value : item;
                if (Array.isArray(raw)) {
                    add(raw[categoryPos], raw[valuePos]);
                }
                else {
                    add(index, raw);
                }
            });
            return;
        }
        const encode = isRecord(serie.encode) ? serie.encode : undefined;
        const valueKey = firstKey(horizontal ? encode?.x : encode?.y);
        if (!valueKey)
            return;
        const categoryKey = firstKey(horizontal ? encode?.y : encode?.x);
        rows.forEach((row, index) => {
            if (!isRecord(row))
                return;
            add(categoryKey ? row[categoryKey] : index, row[valueKey]);
        });
    });
    if (!hasValue)
        return undefined;
    return {
        min: Math.min(0, ...negative.values()),
        max: Math.max(0, ...positive.values()),
    };
};
const roundFloat = (value) => Number(value.toPrecision(12));
// 1-2-5 steps: 12 over 5 ticks -> step 5, max 15
const getNiceStep = (rawStep) => {
    const exponent = Math.floor(Math.log10(rawStep));
    const magnitude = 10 ** exponent;
    const fraction = rawStep / magnitude;
    let niceFraction = 10;
    if (fraction <= 1)
        niceFraction = 1;
    else if (fraction <= 2)
        niceFraction = 2;
    else if (fraction <= 5)
        niceFraction = 5;
    return roundFloat(niceFraction * magnitude);
};
/**
 * One rounded domain for the whole group: min 0 (or below, if any member
 * goes negative), a nice step and a max that is a whole number of steps.
 * All-zero or empty groups get 0..5 so the axis still draws.
 */
export const getLdSharedAxisDomain = (extents, { integerTicks = true, splitNumber = 5 } = {}) => {
    const hi = Math.max(0, ...extents.map((e) => e.max));
    const lo = Math.min(0, ...extents.map((e) => e.min));
    const span = hi - lo;
    if (span <= 0) {
        return integerTicks
            ? { min: 0, max: 5, interval: 1 }
            : { min: 0, max: 1, interval: 0.2 };
    }
    let interval = getNiceStep(span / splitNumber);
    if (integerTicks)
        interval = Math.max(1, Math.ceil(interval));
    return {
        min: roundFloat(Math.floor(lo / interval) * interval),
        max: roundFloat(Math.ceil(hi / interval) * interval),
        interval,
    };
};
/**
 * Pins one value axis of a final ECharts option to the shared domain.
 * Other axes (e.g. a percent axis on the right) are left alone.
 */
export const applyLdAxisDomain = (option, axisIndex, domain) => {
    if (!isRecord(option))
        return option;
    const axisKey = isLdHorizontalChart(option) ? 'xAxis' : 'yAxis';
    const axisValue = option[axisKey];
    const axes = asArray(axisValue);
    const target = axes[axisIndex];
    if (!isRecord(target))
        return option;
    const pinned = {
        ...target,
        min: domain.min,
        max: domain.max,
        interval: domain.interval,
        scale: false,
    };
    delete pinned.splitNumber;
    delete pinned.minInterval;
    delete pinned.maxInterval;
    const nextAxes = axes.map((axis, index) => index === axisIndex ? pinned : axis);
    return {
        ...option,
        [axisKey]: Array.isArray(axisValue) ? nextAxes : nextAxes[0],
    };
};
/** Grid `top` in pixels for a container of the given height */
export const getLdGridTopPx = (top, containerHeight) => {
    if (typeof top === 'number' && Number.isFinite(top))
        return top;
    if (typeof top === 'string') {
        const trimmed = top.trim();
        const n = parseFloat(trimmed);
        if (Number.isFinite(n)) {
            return trimmed.endsWith('%') ? (n / 100) * containerHeight : n;
        }
    }
    // ECharts default grid.top
    return 60;
};
/**
 * Moves the top edge of the (first) grid down by `extraPx`, which shrinks
 * the plot area by exactly that much: axis labels keep their size, so the
 * extra space comes out of the plot. `top` is always written in pixels so
 * the caller can tell which option a rendered frame belongs to.
 */
export const applyLdGridTopOffset = (option, extraPx, containerHeight) => {
    if (!isRecord(option))
        return { option, topPx: 0 };
    const gridValue = option.grid;
    const grids = asArray(gridValue);
    const first = isRecord(grids[0]) ? grids[0] : {};
    const topPx = roundFloat(getLdGridTopPx(first.top, containerHeight) + Math.max(0, extraPx));
    const nextFirst = { ...first, top: topPx };
    const nextGrids = grids.length
        ? grids.map((grid, index) => (index === 0 ? nextFirst : grid))
        : [nextFirst];
    return {
        option: {
            ...option,
            grid: Array.isArray(gridValue) ? nextGrids : nextGrids[0],
        },
        topPx,
    };
};
//# sourceMappingURL=ldAxisScaleGroups.js.map